import React, { useState, useEffect } from 'react';
import { Meter, StopRecord } from '../../types';
import { BoltIcon, CogIcon, ArrowRightCircleIcon, StopCircleIcon, ClockIcon, DocumentDuplicateIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface MeterCardProps {
  meter: Meter;
  activeStop: StopRecord | undefined;
  onStartStop: (meterId: string) => void;
  onEndStop: (recordId: string) => void;
  disabled?: boolean;
}

const formatDuration = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const MeterCard: React.FC<MeterCardProps> = ({ meter, activeStop, onStartStop, onEndStop, disabled }) => {
  const isStopped = !!activeStop;
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isStopped && activeStop) {
      setCurrentValue(activeStop.startValue);

      const update = () => {
        const seconds = Math.floor((Date.now() - activeStop.startTime) / 1000);
        setElapsedTime(seconds);
        // Mantenemos el valor inicial constante por ahora hasta que se enlace con la API real en tiempo real.
        setCurrentValue(activeStop.startValue);
      };

      update();
      timer = setInterval(update, 1000);

    } else {
      setElapsedTime(0);
      setCurrentValue(null);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isStopped, activeStop]);

  const handleCopyId = () => {
    navigator.clipboard.writeText(meter.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`bg-white rounded-2xl p-6 transition-all duration-300 border ${isStopped
      ? 'border-red-200 shadow-sm hover:shadow-md hover:border-red-300'
      : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
      }`}>
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-brand-primary transition-colors line-clamp-1">{meter.name}</h3>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Área: <span className="font-medium text-gray-700">{meter.area}</span> <span className="mx-1">•</span> {meter.type}</p>
        </div>
        <div className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wider ${isStopped
          ? 'bg-red-50 text-red-600 border border-red-100'
          : 'bg-green-50 text-green-600 border border-green-100'
          }`}>
          {isStopped ? 'Detenido' : 'En Operación'}
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

        {/* ID Container with Copy Button */}
        <div className="flex items-center space-x-1.5 text-gray-500 bg-gray-50 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-100 transition-colors hover:bg-gray-100">
          <CogIcon className="h-4 w-4 text-gray-400" />
          <span>ID del Equipo</span>
          <button
            onClick={handleCopyId}
            title="Copiar ID"
            className="ml-2 focus:outline-none hover:text-brand-primary transition-colors"
          >
            {copied ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
            ) : (
              <DocumentDuplicateIcon className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Action Buttons */}
        <div>
          {isStopped ? (
            <button
              onClick={() => onEndStop(activeStop.id)}
              disabled={disabled}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs sm:text-sm bg-red-600 text-white font-semibold rounded-lg shadow-sm hover:bg-red-700 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-95 transform'}`}
            >
              <StopCircleIcon className="h-4 w-4" />
              <span>Fin de Parada</span>
            </button>
          ) : (
            <button
              onClick={() => onStartStop(meter.id)}
              disabled={disabled}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs sm:text-sm bg-gray-100 hover:bg-[#E2B53F] text-gray-700 hover:text-gray-900 border border-gray-200 hover:border-[#E2B53F] font-bold rounded-lg shadow-sm transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-95 transform'}`}
            >
              <ArrowRightCircleIcon className="h-4 w-4 stroke-2" />
              <span>Inicio de Parada</span>
            </button>
          )}
        </div>
      </div>
      {isStopped && activeStop && (
        <div className="mt-6 pt-5 border-t border-gray-100 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Transcurrido</span>
            <span className="font-black text-base sm:text-lg text-red-600 flex items-center"><ClockIcon className="h-5 w-5 mr-1.5 stroke-2" />{formatDuration(elapsedTime)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Totalizador</span>
            <span className="font-bold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">{currentValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-gray-500 text-sm">{meter.unit}</span></span>
          </div>
          <div className="pt-2">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1 block">Motivo Activo</span>
            <p className="text-sm font-medium text-gray-800 bg-red-50 p-2.5 rounded-lg border border-red-100">{activeStop.reason}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeterCard;
